import React, { useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material'
import {
  IconArrowLeft,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandLinkedin,
  IconCheck,
  IconExternalLink,
  IconFileDescription,
  IconX,
} from '@tabler/icons-react'
import { useNavigate, useParams } from 'react-router-dom'
import { Card, ContentTitle, PageLoader, Status } from '~/components'
import { toast } from 'react-toastify'
import { RejectModal, ApproveModal } from './components'
import {
  EAffiliatePartnerUserFileType,
  EAffiliatePartnerUserStatus,
  usePartnerAffiliateUserApproveMutation,
  usePartnerAffiliateUserGetQuery,
} from '~/graphql/types'
import { colorAffiliatePartnerUserStatus, displayCpfCnpj, displayPhone, formatDate } from '~/utils'

const fileTypeLabel: Record<EAffiliatePartnerUserFileType, string> = {
  [EAffiliatePartnerUserFileType.selfie]: 'Selfie',
  [EAffiliatePartnerUserFileType.addressProof]: 'Comprovante de endereço',
  [EAffiliatePartnerUserFileType.idDocument]: 'Documento de identidade',
  [EAffiliatePartnerUserFileType.cnpj]: 'CNPJ',
  [EAffiliatePartnerUserFileType.socialContract]: 'Contrato social',
}

const statusLabel: Record<EAffiliatePartnerUserStatus, string> = {
  [EAffiliatePartnerUserStatus.underAnalysis]: 'Em análise',
  [EAffiliatePartnerUserStatus.approved]: 'Aprovado',
  [EAffiliatePartnerUserStatus.rejected]: 'Reprovado',
}

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme()
  return (
    <Typography
      color={theme.palette.grey[800]}
      sx={{ fontWeight: 600, fontSize: '18px', padding: '1.5rem 0 .75rem 0' }}
    >
      {children}
    </Typography>
  )
}

const InfoItem: React.FC<{ label: string; value?: string | null; children?: React.ReactNode }> = ({ label, value, children }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 0.25, fontWeight: 600, textTransform: 'uppercase', fontSize: '0.7rem' }}>
      {label}
    </Typography>
    {children ?? (
      <Typography variant='body1' fontWeight={500}>
        {value || '—'}
      </Typography>
    )}
  </Box>
)

export const PartnerUserAnalysis: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const theme = useTheme()
  const [rejectModalOpen, setRejectModalOpen] = useState(false)
  const [approveModalOpen, setApproveModalOpen] = useState(false)

  const { data, loading, refetch } = usePartnerAffiliateUserGetQuery({
    variables: { id: id || '' },
    skip: !id,
    fetchPolicy: 'no-cache',
    onError(err) {
      toast.error(err.message)
    }
  })

  if (loading) return <PageLoader />

  const user = data?.partnerAffiliateUserGet
  if (!user) return null

  const statusText = statusLabel[user.status]
  const statusColors = colorAffiliatePartnerUserStatus(user.status)
  const isUnderAnalysis = user.status === EAffiliatePartnerUserStatus.underAnalysis

  const address = user.address
  const fullAddress = address
    ? [
        address.street,
        address.number,
        address.complement,
        address.neighborhood,
        address.city,
        address.state,
        address.zipcode,
      ]
        .filter(Boolean)
        .join(', ')
    : null

  return (
    <div>
      <Card sx={{ background: 'white', padding: '0 1rem 1rem 1rem', borderRadius: '1rem' }}>
        <ContentTitle
          title='Análise de preposto'
          description='Visualize os dados do preposto e aprove ou recuse o cadastro'
          breadcrumbLinks={{
            currentLink: 'Análise de preposto',
            links: [
              { href: '/app/partners', label: 'Representantes' },
              { href: '/app/partners/users', label: 'Prepostos' },
            ],
          }}
        />

        {/* Status badge */}
        <Box sx={{ mb: 1 }}>
          <Status
            text={statusText}
            color={statusColors.color}
            background={statusColors.background}
          />
        </Box>

        {/* Dados do Representante Comercial */}
        <SectionTitle>Representante Comercial</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <InfoItem label='Nome'>
              {user.partnerRef ? (
                <Link
                  href={`/app/partners/update/${user.partnerRef.id}`}
                  target='_blank'
                  rel='noopener noreferrer'
                  sx={{ fontWeight: 500, color: 'inherit', textDecorationColor: 'inherit' }}
                >
                  {user.partnerRef.name}
                </Link>
              ) : (
                <Typography variant='body1' fontWeight={500}>—</Typography>
              )}
            </InfoItem>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoItem label='E-mail' value={user.partnerRef?.email} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoItem label='Telefone' value={user.partnerRef?.phone ? displayPhone(user.partnerRef.phone) : null} />
          </Grid>
        </Grid>
        <Divider sx={{ my: 1 }} />

        {/* Dados do Preposto */}
        <SectionTitle>Dados do Preposto</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <InfoItem label='Nome' value={user.name} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoItem label='CPF' value={user.cpf ? displayCpfCnpj(user.cpf) : null} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoItem label='E-mail' value={user.email} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoItem label='Telefone' value={user.phone ? displayPhone(user.phone) : null} />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <InfoItem label='Data de cadastro' value={formatDate(user.createdAt)} />
          </Grid>
        </Grid>

        {/* Endereço */}
        <Divider sx={{ my: 1 }} />
        <SectionTitle>Endereço</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={8}>
            <InfoItem label='Endereço completo' value={fullAddress} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <InfoItem label='País' value={address?.country} />
          </Grid>
        </Grid>

        {/* Redes Sociais */}
        <Divider sx={{ my: 1 }} />
        <SectionTitle>Redes Sociais</SectionTitle>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <InfoItem label='LinkedIn'>
              {user.linkedin ? (
                <Link href={user.linkedin} target='_blank' rel='noopener noreferrer'
                  sx={{ display: 'flex', alignItems: 'center', gap: .5, fontWeight: 500, color: 'inherit', textDecorationColor: 'inherit' }}>
                  <IconBrandLinkedin size={18} />
                  LinkedIn
                  <IconExternalLink size={14} />
                </Link>
              ) : (
                <Typography variant='body1' fontWeight={500}>—</Typography>
              )}
            </InfoItem>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InfoItem label='Instagram'>
              {user.instagram ? (
                <Link href={user.instagram} target='_blank' rel='noopener noreferrer'
                  sx={{ display: 'flex', alignItems: 'center', gap: .5, fontWeight: 500, color: 'inherit', textDecorationColor: 'inherit' }}>
                  <IconBrandInstagram size={18} />
                  Instagram
                  <IconExternalLink size={14} />
                </Link>
              ) : (
                <Typography variant='body1' fontWeight={500}>—</Typography>
              )}
            </InfoItem>
          </Grid>
          <Grid item xs={12} sm={4}>
            <InfoItem label='Facebook'>
              {user.facebook ? (
                <Link href={user.facebook} target='_blank' rel='noopener noreferrer'
                  sx={{ display: 'flex', alignItems: 'center', gap: .5, fontWeight: 500, color: 'inherit', textDecorationColor: 'inherit' }}>
                  <IconBrandFacebook size={18} />
                  Facebook
                  <IconExternalLink size={14} />
                </Link>
              ) : (
                <Typography variant='body1' fontWeight={500}>—</Typography>
              )}
            </InfoItem>
          </Grid>
        </Grid>

        {/* Captação */}
        <Divider sx={{ my: 1 }} />
        <SectionTitle>Captação</SectionTitle>
        <InfoItem label='Como foi realizada a captação' value={user.captureSource} />

        {/* Motivo de reprovação */}
        {user.status === EAffiliatePartnerUserStatus.rejected && (
          <>
            <Divider sx={{ my: 1 }} />
            <SectionTitle>Motivo de Reprovação</SectionTitle>
            {user.rejectionReason ? (
              <Box sx={{ background: theme.palette.error.light, borderRadius: 2, p: 2 }}>
                <Typography color={theme.palette.error.dark}>{user.rejectionReason}</Typography>
              </Box>
            ) : (
              <Typography color='text.secondary' fontStyle='italic'>—</Typography>
            )}
          </>
        )}

        {/* Histórico Profissional */}
        <Divider sx={{ my: 1 }} />
        <SectionTitle>Histórico Profissional</SectionTitle>
        {user.professionalHistory?.length > 0 ? (
          <TableContainer component={Paper} variant='outlined' sx={{ borderRadius: 2 }}>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ background: theme.palette.grey[50] }}>
                  <TableCell sx={{ fontWeight: 600 }}>Empresa</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Segmento</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Cargo</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Observação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {user.professionalHistory.map((item, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{item.companyName || '—'}</TableCell>
                    <TableCell>{item.segment || '—'}</TableCell>
                    <TableCell>{item.role || '—'}</TableCell>
                    <TableCell>{item.observation || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Typography color='text.secondary' fontStyle='italic'>
            Sem histórico profissional
          </Typography>
        )}

        {/* Documentos */}
        <Divider sx={{ my: 1 }} />
        <SectionTitle>Documentos</SectionTitle>
        <Grid container spacing={2}>
          {(Object.keys(fileTypeLabel) as EAffiliatePartnerUserFileType[]).map((type) => {
            const fileItem = user.files?.find((f) => f.type === type)
            const hasFile = !!fileItem?.file?.key

            return (
              <Grid key={type} item xs={12} sm={6} md={4}>
                <Paper
                  variant='outlined'
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    background: !hasFile ? theme.palette.grey[50] : 'transparent',
                    '&:hover': hasFile ? { borderColor: theme.palette.primary.main } : {},
                    transition: 'border-color 0.2s',
                  }}
                >
                  <IconFileDescription size={24} color={hasFile ? theme.palette.primary.main : theme.palette.text.disabled} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                      {fileTypeLabel[type]}
                    </Typography>
                    {hasFile ? (
                      <Link
                        href={`${import.meta.env.VITE_APP_REST_API_URL}files/download/file/${fileItem.file.key}`}
                        target='_blank'
                        rel='noopener noreferrer'
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          fontWeight: 500,
                          fontSize: '0.85rem',
                          color: 'inherit',
                          textDecorationColor: 'inherit',
                        }}
                      >
                        {fileItem.file.fileName || 'Ver arquivo'}
                        <IconExternalLink size={13} />
                      </Link>
                    ) : (
                      <Typography variant='body2' color='text.disabled' fontStyle='italic'>
                        sem documento
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            )
          })}
        </Grid>

        {/* Ações */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', padding: '2rem 0 1rem 0', gap: '1rem', flexWrap: 'wrap' }}>
          <Button
            color='secondary'
            startIcon={<IconArrowLeft />}
            onClick={() => navigate('/app/partners/users')}
          >
            Voltar
          </Button>

          {isUnderAnalysis && (
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <Button
                color='error'
                variant='outlined'
                startIcon={<IconX />}
                onClick={() => setRejectModalOpen(true)}
              >
                Recusar
              </Button>
              <Button
                color='primary'
                startIcon={<IconCheck />}
                onClick={() => setApproveModalOpen(true)}
              >
                Aprovar
              </Button>
            </Box>
          )}
        </Box>
      </Card>

      <RejectModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        userId={id || ''}
        onSuccess={() => refetch()}
      />

      <ApproveModal
        open={approveModalOpen}
        onClose={() => setApproveModalOpen(false)}
        userId={id || ''}
        onSuccess={() => refetch()}
      />
    </div>
  )
}
